/** A desk's lock reason and its notes, in one component on the walk and on the sheet: one labelled line with a More, the rest folded below it. */
import { useState } from "react";
import { REVIEW_WORDS } from "./reviewWords";
import type { DeskNote } from "./deskNotes";

type Note = DeskNote;

/** The sheet shows the same line as the walk: the desk intro is one component in one place (the sheet's old bullet list, the swap fold and the lock line are its lines). */
export function DeskNotes({ lockLine, notes }: { readonly lockLine: string | null; readonly notes: readonly Note[] }) {
  return <div className="gx-decide-desknotes"><DeskNotesLine lockLine={lockLine} notes={notes} /></div>;
}

/**
 * The walk's version, one labelled line: "This desk this round: the lock reason, else the first note", whole, with a More at the end of that line when there is more to say; the rest opens below it, one sentence a line.
 * A single sentence has no More and no bullet; nothing at all when the desk has nothing to say.
 */
export function DeskNotesLine({ lockLine, notes }: { readonly lockLine: string | null; readonly notes: readonly Note[] }) {
  const [open, setOpen] = useState(false);
  const lines: Note[] = [...(lockLine === null ? [] : [{ id: "lock", text: lockLine }]), ...notes];
  const [first, ...rest] = lines;
  if (first === undefined) return null;
  // A note marked `always` and a note that is a list (a cost cut's levels) are said in full beneath the first line, whichever note leads (a lock line can); only the rest sits behind More.
  const shown = rest.filter((n) => n.always === true || n.rows !== undefined);
  const hidden = rest.filter((n) => !shown.includes(n));
  const rowsOf = (n: Note) => n.rows === undefined ? null : <ul className="gx-small gx-muted gx-walk-notes-rows" data-testid="walk.notes.rows">{n.rows.map((r) => <li key={r.label}><b>{r.label}</b> {r.text}</li>)}</ul>;
  return (
    <div className="gx-walk-notes" data-testid="walk.notes">
      <p className="gx-small gx-muted gx-walk-notes-line"><b>This desk this round:</b> {first.text}{hidden.length === 0 ? null : <> <button type="button" className="gx-decide-linkbtn" aria-expanded={open} data-testid="walk.notes.more" onClick={() => setOpen(!open)}>{open ? REVIEW_WORDS.less : REVIEW_WORDS.more} <span aria-hidden="true">{open ? "▴" : "▾"}</span></button></>}</p>
      {"rows" in first ? rowsOf(first) : null}
      {shown.map((n) => <div key={n.id} data-testid="walk.notes.always">{n.rows === undefined ? <p className="gx-small gx-muted gx-walk-notes-line">{n.text}</p> : <><p className="gx-small gx-muted gx-walk-notes-line">{n.text}</p>{rowsOf(n)}</>}</div>)}
      {open && hidden.length > 0 ? <ul className="gx-small gx-muted gx-walk-notes-rest" data-testid="walk.notes.rest">{hidden.map((n) => <li key={n.id}>{n.text}</li>)}</ul> : null}
    </div>
  );
}
